<template>
  <div class="v3-data-table">
    <div class="py-3">
      <InfoBox
        v-if="state.serverRes"
        :class="{ danger: state.hasError }"
        :title="state.serverRes.title"
      >
        {{ state.serverRes.message }}
      </InfoBox>
    </div>
    <table v-if="state.reOrderedPeriods" class="table bg-white">
      <thead>
        <tr>
          <th class="period-table-th position-relative" />
          <th class="period-table-th position-relative"><div>Name</div></th>
          <th class="period-table-th position-relative">
            <div>Status</div>
          </th>
          <th class="period-table-th position-relative" style="max-width: 50px">
            <div class="text-end">Action</div>
          </th>
        </tr>
      </thead>
      <draggable
        v-model="state.reOrderedPeriods"
        item-key="id"
        filter=".no-drag"
        tag="tbody"
        @end="saveNewPeriodsOrder"
      >
        <template #item="{ element, index }">
          <tr class="w-100 bg-white">
            <td style="width: 100px" class="mw-auto text-center">
              <div
                class="d-flex align-items-center justify-content-center gap-2 px-2 py-1 rounded"
              >
                <i class="ri-drag-move-2-line text-value-xl"></i>
                <IconButton
                  :icon="'ri-arrow-up-s-line'"
                  :disabled="index === 0"
                  :aria-label="'Move period up'"
                  :tooltip="'Move period up'"
                  @click="moveUp(index)"
                />
                <IconButton
                  :icon="'ri-arrow-down-s-line'"
                  :disabled="index === state.reOrderedPeriods.length - 1"
                  :aria-label="'Move period down'"
                  :tooltip="'Move period down'"
                  @click="moveDown(index)"
                />
              </div>
            </td>
            <td>{{ element.name }}</td>
            <td>
              <LabelPill
                :class="`status-${element.status ? 'active' : 'inactive'}`"
                :icon="
                  element.status
                    ? 'ri-checkbox-circle-fill'
                    : 'ri-close-circle-fill'
                "
              >
                {{ element.status ? "Active" : "Inactive" }}
              </LabelPill>
            </td>
            <td style="width: 50px">
              <div class="text-end">
                <Actions
                  :actions="tableActions"
                  @click-action="handleTableAction($event, element, index + 1)"
                ></Actions>
              </div>
            </td>
          </tr>
        </template>
      </draggable>
    </table>
  </div>
</template>

<script>
import CreatePeriodForm from "@/v3components/Forms/CreatePeriodForm.vue"
import InfoBox from "@/v3components/shared/Alerts/InfoBox.vue"
import IconButton from "@/v3components/shared/Buttons/IconButton.vue"
import LabelPill from "@/v3components/shared/Buttons/LabelPill.vue"
import Actions from "@/v3components/shared/DataTable/Actions.vue"
import { computed, inject, onMounted, reactive, watch } from "vue"
import draggable from "vuedraggable"
import { useStore } from "vuex"

export default {
  components: {
    draggable,
    Actions,
    LabelPill,
    IconButton,
    InfoBox
  },
  setup() {
    const store = useStore()
    const state = reactive({
      savingPeriods: false,
      reOrderedPeriods: [],
      serverRes: null,
      hasError: false
    })

    const actionDialog = inject("actionDialog")
    const modal = inject("modal")

    const periods = computed(() => store.getters["periods/periods"])

    const onDeletePeriod = (item) => {
      actionDialog.open(deletePeriod, {
        args: item,
        props: { danger: true, title: "Delete", question: "Are you sure?" }
      })
    }

    const onEditPeriod = (item) => {
      store.commit("periods/SET_EDITABLE_PERIOD", item)
      showPeriodForm(item)
    }

    const showPeriodForm = () => {
      modal.open(CreatePeriodForm, {
        size: "lg",
        title: "Edit period",
        props: {
          editableMode: true
        }
      })
    }

    const deletePeriod = (period) => {
      store
        .dispatch("periods/deletePeriod", { periodId: period.id })
        .then(() => {
          setTimeout(() => {
            store.dispatch("periods/getPeriods", {
              clearCacheEntry: true
            })
          }, 750)
        })
    }

    const setResponseInfoBox = (title, message, isError = false) => {
      if (title || message) {
        state.serverRes = { title, message }
        state.hasError = isError
      } else {
        state.serverRes = null
        state.hasError = false
      }
    }

    const saveNewPeriodsOrder = async () => {
      if (state.savingPeriods) return

      state.savingPeriods = true
      const data = state.reOrderedPeriods.map((period, i) => {
        return {
          id: period.id,
          position: i + 1
        }
      })
      try {
        await store.dispatch("periods/updatePosition", data)
        setResponseInfoBox("Success", "Period order updated successfully!")
        setTimeout(() => setResponseInfoBox(), 2000)
      } catch (err) {
        const message =
          err?.response?.data?.message || "Failed to update period order"
        setResponseInfoBox("Error", message, true)
        setTimeout(() => setResponseInfoBox(), 3000)
      } finally {
        state.savingPeriods = false
      }
    }

    const moveUp = (index) => {
      if (index > 0) {
        ;[state.reOrderedPeriods[index], state.reOrderedPeriods[index - 1]] = [
          state.reOrderedPeriods[index - 1],
          state.reOrderedPeriods[index]
        ]
        saveNewPeriodsOrder()
      }
    }

    const moveDown = (index) => {
      if (index < state.reOrderedPeriods.length - 1) {
        ;[state.reOrderedPeriods[index], state.reOrderedPeriods[index + 1]] = [
          state.reOrderedPeriods[index + 1],
          state.reOrderedPeriods[index]
        ]
        saveNewPeriodsOrder()
      }
    }

    watch(
      () => periods.value,
      () => {
        state.reOrderedPeriods = periods.value ? [...periods.value] : []
      }
    )

    const tableActions = [
      { label: "Edit", icon: "ri-checkbox-circle-line", action: "edit" },
      {
        label: "Delete",
        icon: "ri-delete-bin-line",
        class: "important",
        action: "delete"
      }
    ]
    const handleTableAction = (action, item) => {
      if (action.action === "delete") {
        onDeletePeriod(item)
      } else {
        onEditPeriod(item)
      }
    }

    onMounted(() => {
      store.dispatch("periods/getPeriods").then(() => {
        if (periods.value) {
          state.reOrderedPeriods = [...periods.value]
        }
      })
    })

    return {
      periods,
      saveNewPeriodsOrder,
      state,
      tableActions,
      handleTableAction,
      moveUp,
      moveDown
    }
  }
}
</script>

<style lang="scss" scoped>
.v3-data-table {
  padding: 0 8px !important;
  table {
    thead {
      tr:first-child {
        th:first-child {
          &::after {
            border-right: 0 !important;
          }
        }
      }
    }
  }
}
</style>
